import React from 'react';
import { Label, Loader, Message, Segment, SemanticCOLORS, Table } from 'semantic-ui-react';
import styled from 'styled-components';

import { maskCpfOrCnpj, maskMoney } from '../../../services/masks';
import { StandaloneChargeDTO, StandaloneChargeStatusEnum } from '../../../services/standalone-charges';
import TablePaginationMenu from '../../../components/TablePaginationMenu';

import { ListActionType } from '../types';
import ChargeActionsMenu from './ChargeActionsMenu';

const statusLabelColor: Record<string, string> = {
  [StandaloneChargeStatusEnum.pendingPayment]: 'yellow',
  [StandaloneChargeStatusEnum.paid]: 'green',
  [StandaloneChargeStatusEnum.refunded]: 'grey',
  [StandaloneChargeStatusEnum.cancelled]: 'red',
  [StandaloneChargeStatusEnum.expired]: 'grey'
};

const DesktopTable = styled.div`
  overflow-x: auto;

  @media (max-width: 768px) {
    display: none;
  }
`;

const DesktopPagination = styled.div`
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileChargesList = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
`;

const MobileChargeCard = styled.article`
  overflow: hidden;
  border: 1px solid #dfe0e2;
  border-radius: 6px;
  background: #eaeaea;
`;

const MobileChargeHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px;
  background: #ffffff;
`;

const MobilePayer = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  color: #2b313c;
  font-size: 16px;
  line-height: 1.25;
`;

const MobilePayerName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MobilePayerDocument = styled.span`
  color: #aaadb1;
  font-size: 12px;
`;

const MobileCreatedAt = styled.time`
  flex-shrink: 0;
  color: #6b6f77;
  font-size: 14px;
  white-space: nowrap;
`;

const MobileChargeDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px;
  background: #ffffff;
`;

const MobileChargeDetail = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 152px) minmax(0, 1fr);
  gap: 12px;
  color: #2b313c;
  font-size: 16px;
  line-height: 1.25;
`;

const MobileChargeDetailLabel = styled.span`
  color: #6b6f77;
`;

const MobileChargeDetailValue = styled.span`
  min-width: 0;
  overflow-wrap: anywhere;
`;

const MobileChargeFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px;
  background: #ffffff;
`;

const MobileStatus = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 4px 12px;
  border-radius: 8px;
  background: #dfe0e2;
  color: #2b313c;
  font-size: 14px;
`;

const MobileChargeActions = styled.div`
  .ui.menu {
    min-height: 0;
    margin: 0;
    border: 0;
    box-shadow: none;
  }

  .ui.menu > .item {
    padding: 0;
  }

  .ui.dropdown > .icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    margin: 0;
    border: 1px solid #dfe0e2;
    border-radius: 6px;
    color: #59249b;
  }
`;

const MobileEmptyState = styled.div`
  padding: 16px;
  border: 1px solid #dfe0e2;
  border-radius: 6px;
  background: #ffffff;
`;

const MobilePagination = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    gap: 24px;
    padding: 32px 0;
  }
`;

const MobilePaginationButton = styled.button`
  flex: 1;
  min-width: 0;
  height: 46px;
  padding: 12px 16px;
  border: 0;
  border-radius: 4px;
  background: #59249b;
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    background: #6b7280;
    cursor: not-allowed;
  }
`;

type Props = {
  isLoading: boolean;
  items: StandaloneChargeDTO[];
  statusLabelMap: Record<string, string>;
  currentPage: number;
  total: number;
  setCurrentPage: (page: number) => void;
  actionLoading: { chargeId: string; action: ListActionType } | null;
  userCanSend: boolean;
  userCanCancel: boolean;
  userCanRenew: boolean;
  userCanRefund: boolean;
  /** Quando false (ex.: usuário de imobiliária), oculta a coluna Imobiliária. */
  showEstateColumn: boolean;
  onRowClick: (chargeId: string) => void;
  onCopyLink: (link: string, chargeId: string) => void;
  onResendEmail: (id: string) => void;
  onCancel: (charge: StandaloneChargeDTO) => void;
  onRenew: (id: string) => void;
  onRefund: (charge: StandaloneChargeDTO) => void;
};

const stopRowOpen = (e: React.SyntheticEvent) => {
  e.stopPropagation();
};

const formatChargeDateTime = (value?: string | null): string => {
  if (!value) return '-';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';

  return `${date.toLocaleDateString('pt-BR')} • ${date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit'
  })}`;
};

const getFormattedAmount = (amount: number | string | null | undefined): string =>
  `R$ ${maskMoney(String(Number(amount || 0).toFixed(2)), true)}`;

const ChargesTable: React.FC<Props> = ({
  isLoading,
  items,
  statusLabelMap,
  currentPage,
  total,
  setCurrentPage,
  actionLoading,
  userCanSend,
  userCanCancel,
  userCanRenew,
  userCanRefund,
  showEstateColumn,
  onRowClick,
  onCopyLink,
  onResendEmail,
  onCancel,
  onRenew,
  onRefund
}) => {
  const columnCount = showEstateColumn ? 9 : 8;
  const lastPage = Math.ceil(total / 10);
  const canGoBack = currentPage > 1;
  const canGoForward = total > 0 && currentPage < lastPage;

  const renderMobileActions = (item: StandaloneChargeDTO) => {
    const isCopyLoading =
      actionLoading?.chargeId === item.id && actionLoading.action === 'copy';
    const isResendLoading =
      actionLoading?.chargeId === item.id && actionLoading.action === 'resend';
    const isCancelLoading =
      actionLoading?.chargeId === item.id && actionLoading.action === 'cancel';
    const isRenewLoading =
      actionLoading?.chargeId === item.id && actionLoading.action === 'renew';

    return (
      <MobileChargeActions onClick={stopRowOpen}>
        <ChargeActionsMenu
          charge={item}
          canSend={userCanSend}
          canCancel={userCanCancel}
          canRenew={userCanRenew}
          canRefund={userCanRefund}
          loading={
            isCopyLoading ||
            isResendLoading ||
            isCancelLoading ||
            isRenewLoading ||
            Boolean(actionLoading)
          }
          onCopyLink={onCopyLink}
          onResendEmail={onResendEmail}
          onCancel={onCancel}
          onRenew={onRenew}
          onRefund={onRefund}
        />
      </MobileChargeActions>
    );
  };

  const renderPagination = () => (
    <>
      <DesktopPagination>
        <TablePaginationMenu
          currentPage={currentPage}
          dataCount={total}
          setCurrentPage={setCurrentPage}
        />
      </DesktopPagination>
      <MobilePagination>
        <MobilePaginationButton
          type="button"
          disabled={!canGoBack}
          onClick={() => setCurrentPage(currentPage - 1)}
        >
          Voltar
        </MobilePaginationButton>
        <MobilePaginationButton
          type="button"
          disabled={!canGoForward}
          onClick={() => setCurrentPage(currentPage + 1)}
        >
          Próximo
        </MobilePaginationButton>
      </MobilePagination>
    </>
  );

  if (isLoading) {
    return (
      <>
        <Segment basic style={{ minHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Loader active inline="centered">
            Carregando cobranças…
          </Loader>
        </Segment>
        {renderPagination()}
      </>
    );
  }

  return (
    <>
      <DesktopTable>
        <Table
          celled
          striped
          unstackable
          selectable
          style={{ minWidth: showEstateColumn ? 980 : 840 }}
          aria-label="Tabela de cobranças"
        >
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Data</Table.HeaderCell>
              <Table.HeaderCell>Expiração</Table.HeaderCell>
              {showEstateColumn && <Table.HeaderCell>Imobiliária</Table.HeaderCell>}
              <Table.HeaderCell>Pagador</Table.HeaderCell>
              <Table.HeaderCell>Método</Table.HeaderCell>
              <Table.HeaderCell>Documento</Table.HeaderCell>
              <Table.HeaderCell textAlign="right">Valor</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell textAlign="center">Ações</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {items.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={columnCount}>
                  <Message info style={{ margin: 0 }}>
                    <Message.Header>Nenhuma cobrança encontrada</Message.Header>
                    <p style={{ margin: '0.5em 0 0' }}>
                      Ajuste os filtros ou crie uma nova cobrança para ela aparecer aqui.
                    </p>
                  </Message>
                </Table.Cell>
              </Table.Row>
            ) : (
              items.map(item => {
                const isCopyLoading =
                  actionLoading?.chargeId === item.id && actionLoading.action === 'copy';
                const isResendLoading =
                  actionLoading?.chargeId === item.id && actionLoading.action === 'resend';
                const isCancelLoading =
                  actionLoading?.chargeId === item.id && actionLoading.action === 'cancel';
                const isRenewLoading =
                  actionLoading?.chargeId === item.id && actionLoading.action === 'renew';
                const isRefunded = item.status === StandaloneChargeStatusEnum.refunded;
                const statusColor = statusLabelColor[item.status] || 'grey';
                const formattedAmount = `R$ ${maskMoney(String(Number(item.amount).toFixed(2)), true)}`;
                const refundedAmount = Number(item.refundedAmount || 0);
                const remainingAmount = Math.max(Number(item.amount) - refundedAmount, 0);

                return (
                  <Table.Row
                    key={item.id}
                    style={{ cursor: 'pointer' }}
                    tabIndex={0}
                    onClick={() => onRowClick(item.id)}
                    onKeyDown={(e: React.KeyboardEvent<HTMLTableRowElement>) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onRowClick(item.id);
                      }
                    }}
                    aria-label={`Abrir detalhes da cobrança de ${item.payerName}`}
                  >
                    <Table.Cell>
                      {item.createdAt ? new Date(item.createdAt).toLocaleString('pt-BR') : '-'}
                    </Table.Cell>
                    <Table.Cell>
                      {item.expiresAt ? new Date(item.expiresAt).toLocaleString('pt-BR') : '-'}
                    </Table.Cell>
                    {showEstateColumn && <Table.Cell>{item.estate?.name || '-'}</Table.Cell>}
                    <Table.Cell>{item.payerName}</Table.Cell>
                    <Table.Cell>{item.paymentMethod || '-'}</Table.Cell>
                    <Table.Cell>{maskCpfOrCnpj(item.payerCpf)}</Table.Cell>
                    <Table.Cell textAlign="right">
                      {isRefunded ? (
                        <div>
                          <div style={{ color: '#9CA3AF', textDecoration: 'line-through' }}>
                            {formattedAmount}
                          </div>
                          <div>
                            {`R$ ${maskMoney(String(remainingAmount.toFixed(2)), true)}`}
                          </div>
                        </div>
                      ) : (
                        formattedAmount
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <Label color={statusColor as SemanticCOLORS} size="small">
                        {statusLabelMap[item.status] || item.status}
                      </Label>
                    </Table.Cell>
                    <Table.Cell textAlign="center" onClick={stopRowOpen}>
                      <ChargeActionsMenu
                        charge={item}
                        canSend={userCanSend}
                        canCancel={userCanCancel}
                        canRenew={userCanRenew}
                        canRefund={userCanRefund}
                        loading={
                          isCopyLoading ||
                          isResendLoading ||
                          isCancelLoading ||
                          isRenewLoading ||
                          Boolean(actionLoading)
                        }
                        onCopyLink={onCopyLink}
                        onResendEmail={onResendEmail}
                        onCancel={onCancel}
                        onRenew={onRenew}
                        onRefund={onRefund}
                      />
                    </Table.Cell>
                  </Table.Row>
                );
              })
            )}
          </Table.Body>
        </Table>
      </DesktopTable>

      <MobileChargesList aria-label="Lista de cobranças">
        {items.length === 0 ? (
          <MobileEmptyState>
            <strong>Nenhuma cobrança encontrada</strong>
            <p style={{ margin: '8px 0 0' }}>
              Ajuste os filtros ou crie uma nova cobrança para ela aparecer aqui.
            </p>
          </MobileEmptyState>
        ) : (
          items.map(item => {
            const isRefunded = item.status === StandaloneChargeStatusEnum.refunded;
            const amount = getFormattedAmount(item.amount);
            const refundedAmount = Number(item.refundedAmount || 0);
            const remainingAmount = Math.max(Number(item.amount) - refundedAmount, 0);

            return (
              <MobileChargeCard
                key={item.id}
                role="button"
                tabIndex={0}
                onClick={() => onRowClick(item.id)}
                onKeyDown={(event: React.KeyboardEvent<HTMLElement>) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onRowClick(item.id);
                  }
                }}
                aria-label={`Abrir detalhes da cobrança de ${item.payerName}`}
              >
                <MobileChargeHeader>
                  <MobilePayer>
                    <MobilePayerName>{item.payerName || '-'}</MobilePayerName>
                    <MobilePayerDocument>{maskCpfOrCnpj(item.payerCpf)}</MobilePayerDocument>
                  </MobilePayer>
                  <MobileCreatedAt>
                    {formatChargeDateTime(item.createdAt)}
                  </MobileCreatedAt>
                </MobileChargeHeader>

                <MobileChargeDetails>
                  {showEstateColumn && (
                    <MobileChargeDetail>
                      <MobileChargeDetailLabel>Imobiliária</MobileChargeDetailLabel>
                      <MobileChargeDetailValue>{item.estate?.name || '-'}</MobileChargeDetailValue>
                    </MobileChargeDetail>
                  )}
                  <MobileChargeDetail>
                    <MobileChargeDetailLabel>Valor</MobileChargeDetailLabel>
                    <MobileChargeDetailValue>
                      {isRefunded ? (
                        <>
                          <span style={{ color: '#9ca3af', textDecoration: 'line-through' }}>
                            {amount}
                          </span>{' '}
                          {getFormattedAmount(remainingAmount)}
                        </>
                      ) : (
                        amount
                      )}
                    </MobileChargeDetailValue>
                  </MobileChargeDetail>
                  <MobileChargeDetail>
                    <MobileChargeDetailLabel>Método</MobileChargeDetailLabel>
                    <MobileChargeDetailValue>{item.paymentMethod || '-'}</MobileChargeDetailValue>
                  </MobileChargeDetail>
                  <MobileChargeDetail>
                    <MobileChargeDetailLabel>Expiração</MobileChargeDetailLabel>
                    <MobileChargeDetailValue>{formatChargeDateTime(item.expiresAt)}</MobileChargeDetailValue>
                  </MobileChargeDetail>
                </MobileChargeDetails>

                <MobileChargeFooter>
                  <MobileStatus>{statusLabelMap[item.status] || item.status}</MobileStatus>
                  {renderMobileActions(item)}
                </MobileChargeFooter>
              </MobileChargeCard>
            );
          })
        )}
      </MobileChargesList>

      {renderPagination()}
    </>
  );
};

export default ChargesTable;
